/**
 * Which of the admin's screens a server-side change makes out of date.
 *
 * Read by `RealtimeProvider` (see `@connect/web-shared`). Each entry maps the
 * server's resource names onto the query keys the screens use; a change to a
 * resource listed here refetches the matching queries on screen and marks the
 * rest stale. A resource not listed changes nothing here.
 *
 * `session` entries re-read `/auth/me`: a role, a policy or an installation
 * service changes what this person may open, and the navigation comes from there.
 */

import type { RealtimeRoutes } from '@connect/web-shared';

import { NAV_BADGES_KEY } from './nav-badges';

export const REALTIME_TABLE: RealtimeRoutes = {
  routes: [
    { resource: 'crm.', keys: [['crm']] },
    // A client adding, editing or syncing its customers.
    { resource: 'core.own_contact', keys: [['customers']] },
    { resource: 'projects.project', keys: [['projects']] },
    { resource: 'projects.milestone', keys: [['projects']] },
    { resource: 'projects.task', keys: [['tasks'], ['projects']] },
    { resource: 'projects.allocation', keys: [['allocations'], ['projects']] },
    { resource: 'support.', keys: [['support']] },
    { resource: 'hr.', keys: [['hr']] },
    { resource: 'iam.', keys: [['iam']], session: true },
    { resource: 'timesheets.', keys: [['timesheets'], ['time-entries']] },
    { resource: 'finance.expense', keys: [['expenses']] },
    { resource: 'finance.', keys: [['finance']] },
    // A client's connections, senders and templates, and what they send.
    { resource: 'messaging.', keys: [['messaging']] },
    { resource: 'core.service', keys: [['services']], session: true },
    // Policies, what is asked of clients, and what clients decided.
    { resource: 'core.legal_document', keys: [['legal']] },
    { resource: 'core.consent_requirement', keys: [['legal']] },
    { resource: 'core.own_consent', keys: [['legal']] },
  ],
  // Every audited change is a new line in the audit log, and the dashboard,
  // the reports and the menu's counts count across everything — a client
  // answering a request is the client's own change, not a messaging one.
  always: [['dashboard'], ['audit'], ['reports'], NAV_BADGES_KEY],
};
